import { useEffect, useMemo, useState, type FormEvent } from "react";
import type { DefinitionDetail, DefinitionUsage, ObjectTypeDetail, State, StateCategory } from "@workcore/schemas";
import { Panel, RowMenu } from "../../ui";
import { askText } from "../../confirm";
import { InlineText, WriteError, plural, toKey, useDefinitionWrite } from "./shared";

const CATEGORIES: StateCategory[] = ["todo", "in_progress", "done", "cancelled"];
const CAT_LABEL: Record<StateCategory, string> = { todo: "to do", in_progress: "in progress", done: "done", cancelled: "cancelled" };
const CAT_COLOUR: Record<StateCategory, string> = { todo: "#8a94a6", in_progress: "#0d5c63", done: "#17794a", cancelled: "#b0b6c0" };

/** States left to right in their order, transitions as arcs: forward above the row, backward below. */
function Diagram({ type }: { type: ObjectTypeDetail }) {
  const states = [...type.states].sort((a, b) => a.sort - b.sort);
  const w = 132;
  const gap = 36;
  const pos = (id: number) => states.findIndex((s) => s.id === id) * (w + gap) + w / 2 + 10;
  const rise = (dx: number) => Math.min(18 + Math.abs(dx) / 9, 90);
  const ups = type.transitions.filter((t) => pos(t.toStateId) > pos(t.fromStateId)).map((t) => rise(pos(t.toStateId) - pos(t.fromStateId)));
  const downs = type.transitions.filter((t) => pos(t.toStateId) < pos(t.fromStateId)).map((t) => rise(pos(t.toStateId) - pos(t.fromStateId)));
  // Room above for the tallest forward arc and below for the deepest backward one, so arcs never leave the drawing.
  const y = 18 + 0.75 * Math.max(0, ...ups) + 10;
  const height = y + 18 + 0.75 * Math.max(0, ...downs) + 14;
  const width = Math.max(states.length * (w + gap) + 20, 300);
  const arcs = type.transitions.map((t) => {
    const x1 = pos(t.fromStateId);
    const x2 = pos(t.toStateId);
    const forward = x2 > x1;
    const h = rise(x2 - x1);
    const yy = forward ? y - 18 : y + 18;
    const cy = forward ? yy - h : yy + h;
    return { key: `${t.fromStateId}-${t.toStateId}`, d: `M ${x1} ${yy} C ${x1} ${cy}, ${x2} ${cy}, ${x2} ${yy}`, forward };
  });
  return (
    <svg className="diagram" viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={`${type.name} state diagram`}>
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#5f6775" />
        </marker>
      </defs>
      {arcs.map((a) => <path key={a.key} d={a.d} fill="none" stroke={a.forward ? "#5f6775" : "#b42318"} strokeWidth={1.3} markerEnd="url(#arrow)" opacity={0.85} />)}
      {states.map((s, i) => (
        <g key={s.id} transform={`translate(${i * (w + gap) + 10}, ${y - 18})`}>
          <rect width={w} height={36} rx={8} fill="#fff" stroke={s.colour ?? CAT_COLOUR[s.category]} strokeWidth={s.isInitial ? 3 : 1.5} />
          <text x={w / 2} y={22} textAnchor="middle" fontSize={13} fill="#151922">{s.name}</text>
        </g>
      ))}
    </svg>
  );
}

function Transitions({ slug, def, type, canEdit }: { slug: string; def: DefinitionDetail; type: ObjectTypeDetail; canEdit: boolean }) {
  const write = useDefinitionWrite(slug, def.id);
  const states = useMemo(() => [...type.states].sort((a, b) => a.sort - b.sort), [type.states]);
  const current = useMemo(() => new Set(type.transitions.map((t) => `${t.fromStateId}-${t.toStateId}`)), [type.transitions]);
  const [pending, setPending] = useState(current);
  useEffect(() => setPending(current), [current]);
  const dirty = pending.size !== current.size || [...pending].some((k) => !current.has(k));
  const toggle = (k: string) => setPending((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const save = () =>
    write.mutate({
      method: "PUT",
      path: `/v1/ws/${slug}/types/${type.id}/transitions`,
      body: { transitions: [...pending].map((k) => { const [f, t] = k.split("-"); return { fromStateId: Number(f), toStateId: Number(t) }; }) },
    });
  return (
    <Panel
      title="Transitions"
      actions={
        <label className="check">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={type.enforceTransitions}
            onChange={(e) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/types/${type.id}`, body: { enforceTransitions: e.target.checked } })}
          />
          enforce (otherwise any state may move to any state)
        </label>
      }
    >
      <Diagram type={type} />
      <table className="matrix">
        <thead>
          <tr>
            <th className="muted small">from ↓ to →</th>
            {states.map((s) => <th key={s.id}>{s.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {states.map((from) => (
            <tr key={from.id}>
              <th>{from.name}</th>
              {states.map((to) => {
                const k = `${from.id}-${to.id}`;
                return (
                  <td key={to.id}>
                    {from.id === to.id ? <span className="muted">·</span> : <input type="checkbox" aria-label={`${from.name} to ${to.name}`} disabled={!canEdit} checked={pending.has(k)} onChange={() => toggle(k)} />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div className="row">
          <button disabled={!dirty || write.isPending} onClick={save}>Save transitions</button>
          {dirty && <button className="secondary" onClick={() => setPending(current)}>Discard</button>}
          {!type.enforceTransitions && <span className="muted small">Not enforced: the allowed moves are recorded but not checked.</span>}
        </div>
      )}
      <WriteError error={write.error} />
    </Panel>
  );
}

function StateRow({ slug, def, type, s, objects, canEdit, index, count }: { slug: string; def: DefinitionDetail; type: ObjectTypeDetail; s: State; objects: number; canEdit: boolean; index: number; count: number }) {
  const write = useDefinitionWrite(slug, def.id);
  const others = type.states.filter((x) => x.id !== s.id);
  const sorted = [...type.states].sort((a, b) => a.sort - b.sort);
  const patch = (body: unknown, impact?: string) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/states/${s.id}`, body, ...(impact ? { impact } : {}) });
  const move = (dir: -1 | 1) => {
    const other = sorted[index + dir];
    if (!other) return;
    // swap sort positions with the neighbour (two writes, each dry-run first)
    write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/states/${s.id}`, body: { sort: other.sort } }, {
      onSuccess: () => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/states/${other.id}`, body: { sort: s.sort } }),
    });
  };
  const remove = () => {
    if (objects === 0) return write.mutate({ method: "DELETE", path: `/v1/ws/${slug}/states/${s.id}`, impact: `Remove state '${s.name}'.` });
    void askText({
      title: `Remove '${s.name}'?`,
      body: `${plural(objects, "object")} ${objects === 1 ? "is" : "are"} in this state and must move first.`,
      action: "Next",
      input: { label: "Move them to (state key)", value: others[0]?.key ?? "", help: `One of: ${others.map((o) => o.key).join(", ")}` },
    }).then((choice) => {
    const target = others.find((o) => o.key === (choice ?? others[0]?.key)?.trim());
    if (choice === undefined && !others[0]) return;
    if (!target) return;
    write.mutate({ method: "DELETE", path: `/v1/ws/${slug}/states/${s.id}`, query: { target_state_id: target.id }, impact: `Remove '${s.name}' and move ${plural(objects, "object")} to '${target.name}'. Each move is recorded and can be reverted.` });
    });
  };
  return (
    <tr>
      <td><span className="dot" style={{ background: s.colour ?? CAT_COLOUR[s.category] }} /> <InlineText label={`${s.name} name`} value={s.name} disabled={!canEdit} onSave={(v) => patch({ name: v })} /></td>
      <td><code>{s.key}</code></td>
      <td>
        <select
          aria-label={`${s.name} category`}
          disabled={!canEdit}
          value={s.category}
          onChange={(e) => patch({ category: e.target.value }, objects > 0 ? `Change the category of '${s.name}'. ${plural(objects, "object")} keep${objects === 1 ? "s" : ""} their actual dates; only future moves follow the new category.` : undefined)}
        >
          {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
        </select>
      </td>
      <td>{s.isInitial ? <span className="badge badge-active">initial</span> : canEdit && <button className="link" onClick={() => patch({ isInitial: true })}>make initial</button>}</td>
      <td>{objects}</td>
      <td className="actions">
        {canEdit && (
          <>
            <button className="link" aria-label={`Move ${s.name} up`} disabled={index === 0} onClick={() => move(-1)}>↑</button>
            <button className="link" aria-label={`Move ${s.name} down`} disabled={index === count - 1} onClick={() => move(1)}>↓</button>
            <input type="color" aria-label={`${s.name} colour`} value={s.colour ?? CAT_COLOUR[s.category]} onChange={(e) => patch({ colour: e.target.value })} />
            <RowMenu label={`Actions for ${s.name}`} items={[!s.isInitial && { label: "Remove…", danger: true, onSelect: remove }]} />
          </>
        )}
        <WriteError error={write.error} />
      </td>
    </tr>
  );
}

export function StatesTab({ slug, def, usage, canEdit }: { slug: string; def: DefinitionDetail; usage: DefinitionUsage | undefined; canEdit: boolean }) {
  const [typeId, setTypeId] = useState<number | undefined>(def.types[0]?.id);
  const type = def.types.find((t) => t.id === typeId) ?? def.types[0];
  const write = useDefinitionWrite(slug, def.id);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<StateCategory>("in_progress");
  if (!type) return <p className="muted">Add an object type first.</p>;
  const sorted = [...type.states].sort((a, b) => a.sort - b.sort);
  const objectsIn = (id: number) => usage?.states.find((s) => s.stateId === id)?.objects ?? 0;
  return (
    <>
      <nav className="tabs secondary-tabs">
        {def.types.map((t) => (
          <button key={t.id} className={t.id === type.id ? "active" : ""} onClick={() => setTypeId(t.id)}>
            {t.name}
          </button>
        ))}
      </nav>
      <Panel title={`${type.name} states`}>
        <table className="grid">
          <thead><tr><th>Name</th><th>Key</th><th>Category</th><th /><th>Objects</th><th /></tr></thead>
          <tbody>
            {sorted.map((s, i) => <StateRow key={s.id} slug={slug} def={def} type={type} s={s} objects={objectsIn(s.id)} canEdit={canEdit} index={i} count={sorted.length} />)}
          </tbody>
        </table>
        {canEdit && (
          <form autoComplete="off"
            className="inline wrap"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              write.mutate({ method: "POST", path: `/v1/ws/${slug}/types/${type.id}/states`, body: { key: toKey(name), name, category } }, { onSuccess: () => setName("") });
            }}
          >
            <label className="inline-field"><span>New state</span><input aria-label="New state name" placeholder="e.g. In Review" required value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="inline-field"><span>Category</span><select aria-label="New state category" value={category} onChange={(e) => setCategory(e.target.value as StateCategory)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
            </select></label>
            <button type="submit">Add state</button>
          </form>
        )}
        <p className="muted small">Categories drive cross-workflow queries and actual dates: entering "in progress" sets the actual start, entering "done" the actual end.</p>
        <WriteError error={write.error} />
      </Panel>
      <Transitions slug={slug} def={def} type={type} canEdit={canEdit} />
    </>
  );
}
